import { computed, inject, Injectable, signal } from '@angular/core';
import { catchError, filter, finalize, map, Observable, of, shareReplay, tap } from 'rxjs';

import { ApiResponse } from '@app/core/api';
import { parseBooleanFieldValue } from '@app/core/utils/field-value.util';
import {
  REGISTRATION_ROUTE_SECTION_STEP,
  RegistrationStepRoute,
} from '@app/features/registration/constants/registration-steps.const';

import { RegistrationApiService } from './registration-api.service';
import { RegistrationWizardService } from './registration-wizard.service';
import {
  CompanyInformationInquiryDto,
  ContactInformationAddressInquiryDto,
  PersonalInformationInquiryDto,
  RegistrationField,
  RegistrationFieldId,
  RegistrationFieldStatus,
  RegistrationResult,
  RegistrationSection,
  RegistrationSectionStatus,
  RegistrationStatus,
  SubmitBasicInformationDto,
  SubmitCompanyInformationDto,
  SubmitContactInformationDto,
  SubmitPersonalInformationDto,
  toSubmitCompanyInformationFormData,
  toSubmitPersonalInformationFormData,
  UpdateBasicInformationDto,
  UpdateBasicInformationIsLegalDto,
  UpdateCompanyInformationDto,
  UpdateContactInformationDto,
  UpdatePersonalInformationDto,
  toUpdateCompanyInformationFormData,
  toUpdatePersonalInformationFormData,
} from '../types';

/**
 * Owns the registration data: the loaded result, loading/submitting flags,
 * field/section selectors, and the API write orchestration. It is the single
 * place that mutates registration state.
 */
@Injectable({ providedIn: 'root' })
export class RegistrationStore {
  private readonly api = inject(RegistrationApiService);
  private readonly wizard = inject(RegistrationWizardService);

  readonly registration = signal<RegistrationResult | null>(null);
  readonly loading = signal(false);
  readonly submitting = signal(false);

  readonly isVerified = computed(() => this.registration()?.isVerified ?? false);
  readonly status = computed(() => this.registration()?.status ?? null);
  readonly sections = computed(() => this.registration()?.sections ?? []);
  private readonly fieldMap = computed(() => {
    const map = new Map<RegistrationFieldId, RegistrationField>();

    for (const section of this.sections()) {
      for (const field of section.fields) {
        map.set(field.id, field);
      }
    }

    return map;
  });
  private readonly rejectedReasonMap = computed(() => {
    const map = new Map<RegistrationFieldId, string>();

    for (const section of this.sections()) {
      for (const field of section.fields) {
        const reason = field.rejectDescription?.trim();

        if (field.status === RegistrationFieldStatus.Rejected && reason) {
          map.set(field.id, reason);
        }
      }
    }

    return map;
  });

  private sessionLoaded = false;
  private inflight$: Observable<boolean> | null = null;

  ensureLoaded(force = false): Observable<boolean> {
    if (!force && this.sessionLoaded && this.registration()) {
      return of(true);
    }

    if (this.inflight$) {
      return this.inflight$;
    }

    this.loading.set(true);

    this.inflight$ = this.api.getRegistration().pipe(
      filter(response => response.success),
      map(response => response.result),
      tap(result => this.applyRegistrationResult(result)),
      map(() => true),
      catchError(() => of(false)),
      finalize(() => {
        this.loading.set(false);
        this.inflight$ = null;
      }),
      shareReplay(1),
    );

    return this.inflight$;
  }

  refresh(): Observable<boolean> {
    return this.ensureLoaded(true);
  }

  applyRegistrationResult(result: RegistrationResult): void {
    this.registration.set(result);
    this.sessionLoaded = true;
    this.syncWizardFromRegistration(result);
  }

  clear(): void {
    this.registration.set(null);
    this.sessionLoaded = false;
    this.inflight$ = null;
    this.wizard.setIsLegalEntity(false);
  }

  updateBasicInformation(dto: UpdateBasicInformationDto): Observable<boolean> {
    return this.applyResultFrom(this.api.updateBasicInformation(dto));
  }

  updateBasicInformationIsLegal(dto: UpdateBasicInformationIsLegalDto): Observable<boolean> {
    return this.applyResultFrom(this.api.updateBasicInformationIsLegal(dto));
  }

  submitBasicInformation(dto: SubmitBasicInformationDto): Observable<boolean> {
    return this.guardSubmit(this.api.submitBasicInformation(dto));
  }

  submitPersonalInformation(dto: SubmitPersonalInformationDto): Observable<boolean> {
    return this.guardSubmit(
      this.api.submitPersonalInformation(toSubmitPersonalInformationFormData(dto)),
    );
  }

  inquiryPersonalInformation(dto: PersonalInformationInquiryDto): Observable<boolean> {
    return this.guardSubmit(this.api.inquiryPersonalInformation(dto));
  }

  updatePersonalInformation(dto: UpdatePersonalInformationDto): Observable<boolean> {
    return this.applyResultFrom(
      this.api.updatePersonalInformation(toUpdatePersonalInformationFormData(dto)),
    );
  }

  submitCompanyInformation(dto: SubmitCompanyInformationDto): Observable<boolean> {
    return this.guardSubmit(
      this.api.submitCompanyInformation(toSubmitCompanyInformationFormData(dto)),
    );
  }

  inquiryCompanyInformation(dto: CompanyInformationInquiryDto): Observable<boolean> {
    return this.guardSubmit(this.api.inquiryCompanyInformation(dto));
  }

  updateCompanyInformation(dto: UpdateCompanyInformationDto): Observable<boolean> {
    return this.applyResultFrom(
      this.api.updateCompanyInformation(toUpdateCompanyInformationFormData(dto)),
    );
  }

  updateContactInformation(dto: UpdateContactInformationDto): Observable<boolean> {
    return this.applyResultFrom(this.api.updateContactInformation(dto));
  }

  submitContactInformation(dto: SubmitContactInformationDto): Observable<boolean> {
    return this.guardSubmit(this.api.submitContactInformation(dto));
  }

  inquiryContactInformationAddress(dto: ContactInformationAddressInquiryDto): Observable<boolean> {
    return this.guardSubmit(this.api.inquiryContactInformationAddress(dto));
  }

  getField(fieldId: RegistrationFieldId): RegistrationField | undefined {
    return this.fieldMap().get(fieldId);
  }

  getFieldValue(fieldId: RegistrationFieldId): string | null {
    return this.getField(fieldId)?.value ?? null;
  }

  getFieldRejectDescription(fieldId: RegistrationFieldId): string | null {
    return this.rejectedReasonMap().get(fieldId) ?? null;
  }

  isFieldEditable(fieldId: RegistrationFieldId): boolean {
    const status = this.getField(fieldId)?.status;

    if (status == null) {
      return true;
    }

    return (
      status === RegistrationFieldStatus.Empty
      || status === RegistrationFieldStatus.PendingByUser
      || status === RegistrationFieldStatus.Rejected
    );
  }

  isFieldVerified(fieldId: RegistrationFieldId): boolean {
    return this.getField(fieldId)?.status === RegistrationFieldStatus.Verified;
  }

  isFieldControlDisabled(fieldId: RegistrationFieldId): boolean {
    if (fieldId === RegistrationFieldId.IsLegal) {
      if (this.isPendingByAdmin()) {
        return true;
      }

      return !this.isFieldEditable(fieldId) && !this.isFieldVerified(fieldId);
    }

    return !this.isFieldEditable(fieldId);
  }

  getSection(step: number): RegistrationSection | undefined {
    return this.sections().find(section => section.step === step);
  }

  getSectionByRoute(route: RegistrationStepRoute): RegistrationSection | undefined {
    return this.getSection(REGISTRATION_ROUTE_SECTION_STEP[route]);
  }

  isSectionNavigable(status: RegistrationSectionStatus): boolean {
    return (
      status !== RegistrationSectionStatus.Inactive
      && status !== RegistrationSectionStatus.PendingByAdmin
    );
  }

  isSectionFinished(route: RegistrationStepRoute): boolean {
    return this.getSectionByRoute(route)?.status === RegistrationSectionStatus.Finished;
  }

  canAdvanceFromSection(route: RegistrationStepRoute): boolean {
    return this.getSectionByRoute(route)?.status !== RegistrationSectionStatus.Inactive;
  }

  canAdvanceWithoutSubmit(route: RegistrationStepRoute): boolean {
    const status = this.getSectionByRoute(route)?.status;

    return status === RegistrationSectionStatus.Finished
      || status === RegistrationSectionStatus.PendingByAdmin;
  }

  isPendingByAdmin(): boolean {
    return this.status() === RegistrationStatus.PendingByAdmin;
  }

  isFinished(): boolean {
    return this.status() === RegistrationStatus.Finished;
  }

  private guardSubmit(
    request$: Observable<ApiResponse<RegistrationResult>>,
  ): Observable<boolean> {
    if (this.submitting()) {
      return of(false);
    }

    this.submitting.set(true);

    return this.applyResultFrom(request$).pipe(
      finalize(() => this.submitting.set(false)),
    );
  }

  private applyResultFrom(
    request$: Observable<ApiResponse<RegistrationResult>>,
  ): Observable<boolean> {
    return request$.pipe(
      filter(response => response.success),
      map(response => response.result),
      tap(result => this.applyRegistrationResult(result)),
      map(() => true),
      catchError(() => of(false)),
    );
  }

  private syncWizardFromRegistration(result: RegistrationResult): void {
    const isLegalField = result.sections
      .flatMap(section => section.fields)
      .find(field => field.id === RegistrationFieldId.IsLegal);

    if (isLegalField == null) {
      return;
    }

    this.wizard.setIsLegalEntity(parseBooleanFieldValue(isLegalField.value));
  }
}
